import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Paper,
  Typography,
  Box,
  Tooltip,
  Collapse,
  Button,
  Menu,
  MenuItem,
  Divider,
  Checkbox,
  Stack,
  Chip,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CancelIcon,
  Delete as DeleteIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  MoreVert as MoreVertIcon,
  SelectAll as SelectAllIcon,
  ContentCopy,
} from '@mui/icons-material';

function LayersPanel({ 
  elements, 
  groups,
  selectedElement,
  selectedElements,
  bulkEditMode,
  onElementsReorder, 
  onElementUpdate, 
  onSelectElement,
  onDeleteElement,
  onToggleElementSelection,
  onSelectAll,
  onDeselectAll,
  onCreateGroup,
  onAddToGroup,
  onRemoveFromGroup,
  onDeleteGroup,
  onGroupUpdate,
  onToggleBulkEdit,
  onDuplicateElement,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [contextMenuTarget, setContextMenuTarget] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverGroup, setDragOverGroup] = useState(null);

  // Group elements by their groupId
  const groupedElements = elements.reduce((acc, element) => {
    const groupId = element.groupId || 'ungrouped';
    if (!acc[groupId]) acc[groupId] = [];
    acc[groupId].push(element);
    return acc;
  }, {});

  const handleMoveUp = (index, groupId = 'ungrouped') => {
    const groupElements = groupedElements[groupId];
    const elementIndex = elements.findIndex(el => el.id === groupElements[index].id);
    if (elementIndex === 0) return;
    
    const newElements = [...elements];
    [newElements[elementIndex - 1], newElements[elementIndex]] = [newElements[elementIndex], newElements[elementIndex - 1]];
    onElementsReorder(newElements);
  };

  const handleMoveDown = (index, groupId = 'ungrouped') => {
    const groupElements = groupedElements[groupId];
    const elementIndex = elements.findIndex(el => el.id === groupElements[index].id);
    if (elementIndex === elements.length - 1) return;
    
    const newElements = [...elements];
    [newElements[elementIndex], newElements[elementIndex + 1]] = [newElements[elementIndex + 1], newElements[elementIndex]];
    onElementsReorder(newElements);
  };

  const startEditing = (element) => {
    setEditingId(element.id);
    setEditingName(element.name || `Layer ${elements.indexOf(element) + 1}`);
  };

  const saveEdit = () => {
    if (editingId && editingName.trim()) {
      onElementUpdate(editingId, { name: editingName.trim() });
    }
    setEditingId(null);
    setEditingName('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleContextMenu = (event, target) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setContextMenuTarget(target);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setContextMenuTarget(null);
  };

  const renderElement = (element, index, groupId = 'ungrouped') => {
    const isSelected = selectedElement === element.id;
    const isChecked = selectedElements.includes(element.id);
    const isGroupSelected = groups.find(g => g.id === selectedElement);
    const showCheckbox = bulkEditMode || (isGroupSelected && element.groupId === isGroupSelected.id);
    
    return (
      <ListItem
        key={element.id}
        draggable={!bulkEditMode && !isGroupSelected}
        onDragStart={(e) => {
          setDraggedItem({ type: 'element', id: element.id });
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => {
          setDraggedItem(null);
          setDragOverGroup(null);
        }}
        onClick={() => {
          if (bulkEditMode || (isGroupSelected && element.groupId === isGroupSelected.id)) {
            onToggleElementSelection(element.id);
          } else {
            onSelectElement(element.id);
          }
        }}
        selected={isSelected && !bulkEditMode && !isGroupSelected}
        onContextMenu={(e) => handleContextMenu(e, { type: 'element', element })}
        sx={{
          bgcolor: isSelected && !bulkEditMode && !isGroupSelected ? 'action.selected' : 
                   isChecked && (bulkEditMode || isGroupSelected) ? 'action.hover' : 'transparent',
          mb: 0.5,
          borderRadius: 1,
          cursor: 'pointer',
          pl: groupId !== 'ungrouped' ? 4 : 2,
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
      >
        {showCheckbox && (
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Checkbox
              checked={isChecked}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onToggleElementSelection(element.id);
              }}
            />
          </ListItemIcon>
        )}
        
        <ListItemIcon sx={{ minWidth: 32 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <IconButton 
              size="small" 
              disabled={index === 0}
              onClick={(e) => {
                e.stopPropagation();
                handleMoveUp(index, groupId);
              }}
              sx={{ p: 0.25 }}
            >
              <MoveUpIcon fontSize="small" />
            </IconButton>
            <IconButton 
              size="small" 
              disabled={index === (groupId === 'ungrouped' ? groupedElements[groupId].length - 1 : groupedElements[groupId].length - 1)}
              onClick={(e) => {
                e.stopPropagation();
                handleMoveDown(index, groupId);
              }}
              sx={{ p: 0.25 }}
            >
              <MoveDownIcon fontSize="small" />
            </IconButton>
          </Box>
        </ListItemIcon>
        
        {editingId === element.id ? (
          <TextField
            id={`layer-name-${element.id}`}
            name={`layerName_${element.id}`}
            label="Layer name"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEdit();
              if (e.key === 'Escape') cancelEdit();
            }}
            onClick={(e) => e.stopPropagation()}
            size="small"
            autoFocus
            sx={{ mr: 1 }}
          />
        ) : (
          <ListItemText 
            primary={element.name || `Layer ${elements.indexOf(element) + 1}`}
            secondary={element.visible ? null : 'Hidden'}
          />
        )}
        
        <ListItemSecondaryAction>
          {editingId === element.id ? (
            <>
              <IconButton size="small" onClick={saveEdit}>
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={cancelEdit}>
                <CancelIcon fontSize="small" />
              </IconButton>
            </>
          ) : (
            <>
              <Tooltip title="Rename">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditing(element);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Tooltip title={element.visible ? "Hide" : "Show"}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onElementUpdate(element.id, { visible: !element.visible });
                  }}
                >
                  {element.visible ? (
                    <VisibilityIcon fontSize="small" />
                  ) : (
                    <VisibilityOffIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Duplicate">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicateElement(element.id);
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
              
              {elements.length > 1 && (
                <Tooltip title="Delete">
                  <IconButton 
                    size="small" 
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteElement(element.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  const renderGroup = (group) => {
    const groupElements = groupedElements[group.id] || [];
    
    // Check if all elements in group are selected
    const allElementIds = groupElements.map(el => el.id);
    const allSelected = allElementIds.every(id => selectedElements.includes(id));
    const someSelected = allElementIds.some(id => selectedElements.includes(id));
    const isGroupSelected = selectedElement === group.id;

    return (
      <Box key={group.id} sx={{ mb: 1 }}>
        <ListItem
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDragOverGroup(group.id);
          }}
          onDragLeave={() => {
            setDragOverGroup(null);
          }}
          onDrop={(e) => {
            e.preventDefault();
            if (draggedItem && draggedItem.type === 'element') {
              onAddToGroup([draggedItem.id], group.id);
            }
            setDragOverGroup(null);
          }}
          onContextMenu={(e) => handleContextMenu(e, { type: 'group', group })}
          onClick={() => {
            if (bulkEditMode) {
              // Toggle selection of all elements in group
              if (allSelected) {
                // Deselect all
                const newSelection = selectedElements.filter(id => !allElementIds.includes(id));
                onDeselectAll();
                newSelection.forEach(id => onToggleElementSelection(id));
              } else {
                // Select all
                allElementIds.forEach(id => {
                  if (!selectedElements.includes(id)) {
                    onToggleElementSelection(id);
                  }
                });
              }
            } else {
              // Select the group itself
              onSelectElement(group.id);
            }
          }}
          sx={{
            bgcolor: dragOverGroup === group.id ? 'action.selected' : 
                     isGroupSelected && !bulkEditMode ? 'action.selected' : 'action.hover',
            borderRadius: 1,
            mb: 0.5,
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            border: dragOverGroup === group.id ? '2px dashed' : 'none',
            borderColor: 'primary.main',
          }}
        >
          {bulkEditMode && (
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Checkbox
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                size="small"
                onClick={(e) => e.stopPropagation()}
              />
            </ListItemIcon>
          )}
          
          <ListItemIcon sx={{ minWidth: 32 }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onGroupUpdate(group.id, { expanded: !group.expanded });
              }}
              sx={{ p: 0.25 }}
            >
              {group.expanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            </IconButton>
          </ListItemIcon>
          
          <ListItemIcon sx={{ minWidth: 32 }}>
            {group.expanded ? (
              <FolderOpenIcon sx={{ color: group.color }} />
            ) : (
              <FolderIcon sx={{ color: group.color }} />
            )}
          </ListItemIcon>
          
          <ListItemText 
            primary={group.name}
            secondary={`${groupElements.length} layer${groupElements.length !== 1 ? 's' : ''}`}
          />
          
          <ListItemSecondaryAction>
            <IconButton
              size="small"
              onClick={(e) => handleContextMenu(e, { type: 'group', group })}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </ListItemSecondaryAction>
        </ListItem>
        
        <Collapse in={group.expanded}>
          <List sx={{ pl: 2 }}>
            {groupElements.map((element, index) => renderElement(element, index, group.id))}
          </List>
        </Collapse>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Layers
        </Typography>
        
        <Stack direction="row" spacing={1}>
          <Tooltip title="Toggle bulk edit mode">
            <Button
              size="small"
              variant={bulkEditMode ? "contained" : "outlined"}
              onClick={onToggleBulkEdit}
              startIcon={<SelectAllIcon />}
            >
              Bulk Edit
            </Button>
          </Tooltip>
          
          <Tooltip title="Create new group">
            <IconButton 
              size="small" 
              onClick={() => {
                const groupName = prompt('Enter group name:', `Group ${groups.length + 1}`);
                if (groupName) {
                  onCreateGroup(groupName);
                }
              }}
            >
              <CreateNewFolderIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      
      {(bulkEditMode || groups.find(g => g.id === selectedElement)) && selectedElements.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={`${selectedElements.length} selected`}
            onDelete={onDeselectAll}
            size="small"
            sx={{ mr: 1 }}
          />
          {bulkEditMode && (
            <Button size="small" onClick={onSelectAll}>
              Select All
            </Button>
          )}
        </Box>
      )}
      
      <List sx={{ bgcolor: 'background.paper' }}>
        {/* Render grouped elements */}
        {groups.map(group => renderGroup(group))}
        
        {/* Render ungrouped elements */}
        {groupedElements.ungrouped && groupedElements.ungrouped.length > 0 && (
          <>
            {groups.length > 0 && <Divider sx={{ my: 1 }} />}
            <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
              Ungrouped Layers
            </Typography>
            {groupedElements.ungrouped.map((element, index) => renderElement(element, index))}
          </>
        )}
      </List>
      
      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        {contextMenuTarget?.type === 'element' && (
          <>
            <MenuItem onClick={() => {
              const selectedIds = bulkEditMode && selectedElements.length > 0 
                ? selectedElements 
                : [contextMenuTarget.element.id];
              const groupName = prompt('Enter group name:', `Group ${groups.length + 1}`);
              if (groupName) {
                const newGroupId = onCreateGroup(groupName);
                onAddToGroup(selectedIds, newGroupId);
              }
              handleCloseMenu();
            }}>
              <CreateNewFolderIcon fontSize="small" sx={{ mr: 1 }} />
              Create Group from Selection
            </MenuItem>
            
            {groups.length > 0 && (
              <>
                <Divider />
                {groups.map(group => (
                  <MenuItem 
                    key={group.id}
                    onClick={() => {
                      const selectedIds = bulkEditMode && selectedElements.length > 0 
                        ? selectedElements 
                        : [contextMenuTarget.element.id];
                      onAddToGroup(selectedIds, group.id);
                      handleCloseMenu();
                    }}
                  >
                    <FolderOpenIcon fontSize="small" sx={{ mr: 1, color: group.color }} />
                    Add to {group.name}
                  </MenuItem>
                ))}
              </>
            )}
            
            {contextMenuTarget.element.groupId && (
              <>
                <Divider />
                <MenuItem onClick={() => {
                  const selectedIds = bulkEditMode && selectedElements.length > 0 
                    ? selectedElements 
                    : [contextMenuTarget.element.id];
                  onRemoveFromGroup(selectedIds);
                  handleCloseMenu();
                }}>
                  Remove from Group
                </MenuItem>
              </>
            )}
          </>
        )}
        
        {contextMenuTarget?.type === 'group' && (
          <>
            <MenuItem onClick={() => {
              const newName = prompt('Enter new group name:', contextMenuTarget.group.name);
              if (newName) {
                onGroupUpdate(contextMenuTarget.group.id, { name: newName });
              }
              handleCloseMenu();
            }}>
              <EditIcon fontSize="small" sx={{ mr: 1 }} />
              Rename Group
            </MenuItem>
            
            <MenuItem onClick={() => {
              onDeleteGroup(contextMenuTarget.group.id);
              handleCloseMenu();
            }}>
              <DeleteIcon fontSize="small" sx={{ mr: 1 }} color="error" />
              Delete Group
            </MenuItem>
          </>
        )}
      </Menu>
    </Paper>
  );
}

export default LayersPanel;